import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Grid,
    IconButton,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Axios from 'axios';
import { tokens } from '../../theme';
import getCookie from '../../utils/getCookies';

const DAYS_OF_WEEK = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
];

const MEETING_TYPES = [
    { value: 'phone', label: 'Phone', icon: PhoneIcon },
    { value: 'in_person', label: 'In Person', icon: LocationOnIcon },
];

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxHeight: '90vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

export default function AvailabilityModal({ open, onClose, availability, admins }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [formData, setFormData] = useState({
        availability_type: 'one_time',
        start_date: '',
        end_date: '',
        start_time: '14:00',
        end_time: '22:00',
        days_of_week: [],
        slot_duration: 15,
        meeting_types: ['phone'],
        assigned_admin_id: '',
        description: '',
        is_active: true,
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [overlapWarning, setOverlapWarning] = useState(null);
    const [otherAdminOverlapWarning, setOtherAdminOverlapWarning] = useState(null);

    // Check for overlaps when relevant fields change
    const checkOverlap = useCallback(async (data) => {
        if (!data.start_date || !data.start_time || !data.end_time) {
            setOverlapWarning(null);
            return;
        }

        const token = getCookie('_auth');
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/admin-availabilities/check-overlap`,
                {
                    assigned_admin_id: data.assigned_admin_id || null,
                    availability_type: data.availability_type,
                    start_date: data.start_date,
                    end_date: data.availability_type === 'recurring' ? data.end_date : null,
                    start_time: data.start_time,
                    end_time: data.end_time,
                    days_of_week: data.availability_type === 'recurring' ? data.days_of_week : null,
                    exclude_id: availability?.id || null,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.has_overlap) {
                setOverlapWarning(response.data.overlapping_availabilities);
            } else {
                setOverlapWarning(null);
            }
            if (response.data.has_other_admin_overlap) {
                setOtherAdminOverlapWarning(response.data.other_admin_overlapping_availabilities);
            } else {
                setOtherAdminOverlapWarning(null);
            }
        } catch (err) {
            console.error('Error checking overlap:', err);
        }
    }, [availability]);

    // Debounced overlap check
    useEffect(() => {
        const timer = setTimeout(() => {
            checkOverlap(formData);
        }, 500);
        return () => clearTimeout(timer);
    }, [
        formData.start_date,
        formData.end_date,
        formData.start_time,
        formData.end_time,
        formData.days_of_week,
        formData.assigned_admin_id,
        formData.availability_type,
        checkOverlap,
    ]);

    useEffect(() => {
        if (availability) {
            setFormData({
                availability_type: availability.availability_type || 'one_time',
                start_date: availability.start_date || '',
                end_date: availability.end_date || '',
                start_time: availability.start_time?.substring(0, 5) || '14:00',
                end_time: availability.end_time?.substring(0, 5) || '22:00',
                days_of_week: availability.days_of_week || [],
                slot_duration: availability.slot_duration || 15,
                meeting_types: availability.meeting_types || ['phone'],
                assigned_admin_id: availability.assigned_admin_id || '',
                description: availability.description || '',
                is_active: availability.is_active !== false,
            });
        } else {
            setFormData({
                availability_type: 'one_time',
                start_date: '',
                end_date: '',
                start_time: '14:00',
                end_time: '22:00',
                days_of_week: [],
                slot_duration: 15,
                meeting_types: ['phone'],
                assigned_admin_id: '',
                description: '',
                is_active: true,
            });
        }
        setError('');
        setOverlapWarning(null);
        setOtherAdminOverlapWarning(null);
    }, [availability, open]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleDaysChange = (event, newDays) => {
        setFormData((prev) => ({ ...prev, days_of_week: newDays || [] }));
    };

    const handleMeetingTypesChange = (event, newTypes) => {
        // Ensure at least one meeting type is selected
        if (newTypes && newTypes.length > 0) {
            setFormData((prev) => ({ ...prev, meeting_types: newTypes }));
        }
    };

    const handleSubmit = async () => {
        setError('');
        setLoading(true);

        // Validation
        if (!formData.start_date) {
            setError('Start date is required');
            setLoading(false);
            return;
        }
        if (!formData.start_time || !formData.end_time) {
            setError('Start time and end time are required');
            setLoading(false);
            return;
        }
        if (formData.start_time >= formData.end_time) {
            setError('End time must be after start time');
            setLoading(false);
            return;
        }
        if (formData.availability_type === 'recurring' && formData.days_of_week.length === 0) {
            setError('Please select at least one day for recurring availability');
            setLoading(false);
            return;
        }
        if (!formData.meeting_types || formData.meeting_types.length === 0) {
            setError('Please select at least one meeting type');
            setLoading(false);
            return;
        }

        const token = getCookie('_auth');
        const payload = {
            ...formData,
            days_of_week: formData.availability_type === 'recurring' ? formData.days_of_week : null,
            end_date: formData.availability_type === 'recurring' ? formData.end_date : null,
            assigned_admin_id: formData.assigned_admin_id || null,
        };

        try {
            if (availability) {
                await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/admin-availabilities/${availability.id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/admin-availabilities`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            onClose(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save availability');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={() => onClose(false)}>
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>
                        {availability ? 'Edit Availability' : 'Add Availability'}
                    </Typography>
                    <IconButton onClick={() => onClose(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Combined Overlap Warning Banner */}
                {((overlapWarning && overlapWarning.length > 0) || (otherAdminOverlapWarning && otherAdminOverlapWarning.length > 0)) && (
                    <Alert
                        severity={overlapWarning && overlapWarning.length > 0 ? "warning" : "info"}
                        icon={<WarningAmberIcon />}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            Time Overlap Detected
                        </Typography>

                        {/* Self Overlap Section */}
                        {overlapWarning && overlapWarning.length > 0 && (
                            <Box sx={{ mb: otherAdminOverlapWarning?.length > 0 ? 1.5 : 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Your existing availability:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                    {overlapWarning.map((overlap, idx) => (
                                        <li key={`self-${idx}`}>
                                            <Typography variant="body2" component="span">
                                                {overlap.type === 'one_time'
                                                    ? `${overlap.start_date}`
                                                    : `Recurring (${overlap.start_date}${overlap.end_date ? ` to ${overlap.end_date}` : '+'})`
                                                }
                                                {' '}{overlap.start_time} - {overlap.end_time}
                                            </Typography>
                                        </li>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Other Admin Overlap Section */}
                        {otherAdminOverlapWarning && otherAdminOverlapWarning.length > 0 && (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Other staff availability:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                    {otherAdminOverlapWarning.map((overlap, idx) => (
                                        <li key={`other-${idx}`}>
                                            <Typography variant="body2" component="span">
                                                <strong>{overlap.admin_name}</strong>:{' '}
                                                {overlap.type === 'one_time'
                                                    ? `${overlap.start_date}`
                                                    : `Recurring (${overlap.start_date}${overlap.end_date ? ` to ${overlap.end_date}` : '+'})`
                                                }
                                                {' '}{overlap.start_time} - {overlap.end_time}
                                            </Typography>
                                        </li>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            {overlapWarning && overlapWarning.length > 0
                                ? "Self-overlaps may cause duplicate slots. Other staff overlaps are informational only."
                                : "Parents will see multiple slots from different staff but won't see staff names."
                            }
                        </Typography>
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* Availability Type */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Availability Type</InputLabel>
                            <Select
                                value={formData.availability_type}
                                label="Availability Type"
                                onChange={handleChange('availability_type')}
                            >
                                <MenuItem value="one_time">One-time</MenuItem>
                                <MenuItem value="recurring">Recurring</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Start Date */}
                    <Grid item xs={formData.availability_type === 'recurring' ? 6 : 12}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={formData.start_date}
                            onChange={handleChange('start_date')}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    {/* End Date (Recurring only) */}
                    {formData.availability_type === 'recurring' && (
                        <Grid item xs={6}>
                            <TextField
                                label="End Date (Optional)"
                                type="date"
                                value={formData.end_date}
                                onChange={handleChange('end_date')}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Leave empty for ongoing"
                            />
                        </Grid>
                    )}

                    {/* Days of Week (Recurring only) */}
                    {formData.availability_type === 'recurring' && (
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 1, color: colors.primary[400] }}>
                                Days of Week *
                            </Typography>
                            <ToggleButtonGroup
                                value={formData.days_of_week}
                                onChange={handleDaysChange}
                                aria-label="days of week"
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {DAYS_OF_WEEK.map((day) => (
                                    <ToggleButton
                                        key={day.value}
                                        value={day.value}
                                        aria-label={day.label}
                                        sx={{
                                            px: 2,
                                            '&.Mui-selected': {
                                                bgcolor: colors.blueAccent[500],
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: colors.blueAccent[600],
                                                },
                                            },
                                        }}
                                    >
                                        {day.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Grid>
                    )}

                    {/* Time inputs with ET indicator */}
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, color: colors.grey[500], fontStyle: 'italic' }}>
                            All times are in Eastern Time (ET)
                        </Typography>
                    </Grid>

                    {/* Start Time */}
                    <Grid item xs={6}>
                        <TextField
                            label="Start Time (ET)"
                            type="time"
                            value={formData.start_time}
                            onChange={handleChange('start_time')}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    {/* End Time */}
                    <Grid item xs={6}>
                        <TextField
                            label="End Time (ET)"
                            type="time"
                            value={formData.end_time}
                            onChange={handleChange('end_time')}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    {/* Slot Duration */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Slot Duration</InputLabel>
                            <Select
                                value={formData.slot_duration}
                                label="Slot Duration"
                                onChange={handleChange('slot_duration')}
                            >
                                <MenuItem value={10}>10 minutes</MenuItem>
                                <MenuItem value={15}>15 minutes</MenuItem>
                                <MenuItem value={20}>20 minutes</MenuItem>
                                <MenuItem value={30}>30 minutes</MenuItem>
                                <MenuItem value={45}>45 minutes</MenuItem>
                                <MenuItem value={60}>60 minutes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Meeting Types (Multi-select) */}
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, color: colors.primary[400] }}>
                            Meeting Types * <span style={{ fontWeight: 'normal', color: colors.grey[500] }}>(select all that apply)</span>
                        </Typography>
                        <ToggleButtonGroup
                            value={formData.meeting_types}
                            onChange={handleMeetingTypesChange}
                            aria-label="meeting types"
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {MEETING_TYPES.map((type) => {
                                const IconComponent = type.icon;
                                return (
                                    <ToggleButton
                                        key={type.value}
                                        value={type.value}
                                        aria-label={type.label}
                                        sx={{
                                            px: 2,
                                            display: 'flex',
                                            gap: 0.5,
                                            '&.Mui-selected': {
                                                bgcolor: colors.blueAccent[500],
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: colors.blueAccent[600],
                                                },
                                            },
                                        }}
                                    >
                                        <IconComponent fontSize="small" />
                                        {type.label}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </Grid>

                    {/* Assigned Admin */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Assign To (Optional)</InputLabel>
                            <Select
                                value={formData.assigned_admin_id}
                                label="Assign To (Optional)"
                                onChange={handleChange('assigned_admin_id')}
                            >
                                <MenuItem value="">Self (Default)</MenuItem>
                                {admins.map((admin) => (
                                    <MenuItem key={admin.id} value={admin.id}>
                                        {admin.first_name} {admin.last_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            label="Description (Optional)"
                            value={formData.description}
                            onChange={handleChange('description')}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="e.g., Parent consultation hours"
                        />
                    </Grid>

                    {/* Is Active (for editing) */}
                    {availability && (
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_active}
                                            onChange={handleChange('is_active')}
                                        />
                                    }
                                    label="Active"
                                />
                            </FormGroup>
                        </Grid>
                    )}
                </Grid>

                {/* Actions */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => onClose(false)}
                        sx={{ borderColor: colors.grey[400], color: colors.grey[600] }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            bgcolor: colors.blueAccent[500],
                            '&:hover': { bgcolor: colors.blueAccent[600] },
                        }}
                    >
                        {loading ? 'Saving...' : availability ? 'Update' : 'Create'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
